/**
 * Step Component Factory
 * HOC that returns appropriate component based on step subtype
 */

import React from 'react';

import type { StepFormMessage } from '../../data-layer/types';

import AddAccess from './AddAccess/AddAccess';
import AccessAttributeMapping from './AddAccessAttributeMapping/AddAccessAttributeMapping';
import BasicConfigStep from './BasicConfigStep';
import ConnectionConfigStep from './ConnectionConfigStep';
import CreateAccountStep from './CreateAccountStep';
import DisableAccountStep from './DisableAccountStep';
import EnableAccountStep from './EnableAccountStep';
import RemoveAccess from './RemoveAccess';
import RemoveAccountStep from './RemoveAccountStep';
import UpdateAccountStep from './UpdateAccountStep';

interface StepComponentFactoryProps {
  message: StepFormMessage;
  onFormSubmit: (
    stepName: string,
    formData: Record<string, any>,
    isValid: boolean
  ) => void;
  onSkip: (stepName: string, nextStepName: string) => void;
}

/**
 * Factory function to get the right step component based on subtype
 */
export function StepComponentFactory({
  message,
  onFormSubmit,
  onSkip,
}: StepComponentFactoryProps) {
  switch (message.subType) {
    case 'basic_config':
      return <BasicConfigStep message={message} onFormSubmit={onFormSubmit} />;

    case 'connection_config':
      return (
        <ConnectionConfigStep message={message} onFormSubmit={onFormSubmit} />
      );

    case 'create_account_form':
      return (
        <CreateAccountStep
          message={message}
          onFormSubmit={onFormSubmit}
          onSkip={onSkip}
        />
      );

    case 'update_account_form':
      return (
        <UpdateAccountStep
          message={message}
          onFormSubmit={onFormSubmit}
          onSkip={onSkip}
        />
      );
    case 'enable_account_form':
      return (
        <EnableAccountStep
          message={message}
          onFormSubmit={onFormSubmit}
          onSkip={onSkip}
        />
      );
    case 'disable_account_form':
      return (
        <DisableAccountStep
          message={message}
          onFormSubmit={onFormSubmit}
          onSkip={onSkip}
        />
      );

    case 'remove_account_form':
      return (
        <RemoveAccountStep
          message={message}
          onFormSubmit={onFormSubmit}
          onSkip={onSkip}
        />
      );
    case 'add_access_form':
      return <AddAccess />;

    case 'remove_access_form':
      return <RemoveAccess onFormSubmit={onFormSubmit} message={message} />;

    case 'accessAttributeMapping':
      return (
        <AccessAttributeMapping message={message} onFormSubmit={onFormSubmit} />
      );

    case 'removeAccess':
      return <RemoveAccess onFormSubmit={onFormSubmit} message={message} />;

    default:
      return (
        <div className='Copilot-UnknownStep'>
          <p>Unknown step type: {message.subType}</p>
          <p>Step: {message.stepName}</p>
        </div>
      );
  }
}

export default StepComponentFactory;
